/*
 * @Description: file content
 * @Author: ljrong
 * @Date: 2024-05-29 14:16:28
 * @LastEditors: Lxc 1533761120@qq.com
 * @LastEditTime: 2025-07-08 17:50:58
 */
import type { AxiosProgressEvent, AxiosResponse, GenericAbortSignal } from 'axios'

import request from './axios'
import { useAuthStore, useRiskWordAppealStore } from '@/store'
import { router } from '@/router'
import { useMessage } from 'naive-ui'

export interface HttpOption {
  url: string
  data?: any
  method?: string
  headers?: any
  options?: {
    showAll?: boolean
    [key: string]: any
  }
  onDownloadProgress?: (progressEvent: AxiosProgressEvent) => void
  signal?: GenericAbortSignal
  beforeRequest?: () => void
  afterRequest?: () => void
}

export interface Response<T = any> {
  model: T
  message: string | null
  resultCode: string
  code: number | null
}

async function http<T = any>(
  { url, data, method, headers, onDownloadProgress, signal, beforeRequest, afterRequest, options }: HttpOption,
) {
  const successHandler = (res: AxiosResponse<Response<T>>) => {
    const authStore = useAuthStore()

    if (res.data.resultCode === 'SUCCESS' || res.data.code === 200) {
      return options && options.showAll ? res.data : res.data.model
    }

    if (res.data.resultCode === 'NOT_LOGIN') {
      console.log('未登录。。。', window.location.search, router.currentRoute.value.fullPath)
      authStore.removeToken()
      if (window.location.search) {
        router.push(`/login${window.location.search}`)
      } else {
        router.push('/login')
      }
    }

    if (res.data.code === 403) {
      // 无权限
      router.push('/workbench')
    }
    if (res.data.code === 91001) {
      // 敏感词检测
      const riskWordAppealStore = useRiskWordAppealStore()
      riskWordAppealStore.updateRiskWordAppealModal(true)
      riskWordAppealStore.updateRiskWords(res.data.model as string[])
    }

    return Promise.reject(res.data).finally(() => {
      if (res.data.code === 91001) {
        // 91001时不出现message
        setTimeout(() => {
          window.$message?.destroyAll()
        }, 0)
      }
    })
  }


  const failHandler = (error: Response<Error>) => {
    afterRequest?.()
    throw new Error(error?.message || 'Error')
  }

  beforeRequest?.()

  method = method || 'GET'

  const params = Object.assign(typeof data === 'function' ? data() : data ?? {}, {})

  // 个人空间下的请求不需要传递 enterpriseId
  if (params.enterpriseId && params.enterpriseId === -1) {
    delete params.enterpriseId
  }

  const result = method === 'GET'
    ? await request.get(url, { params, signal, onDownloadProgress }).then(successHandler, failHandler)
    : await request.post(url, params, { headers, signal, onDownloadProgress }).then(successHandler, failHandler)

  return result
}

export function get<T = any>(
  { url, data, method = 'GET', onDownloadProgress, signal, beforeRequest, afterRequest, options }: HttpOption,
): Promise<T> {
  return http<T>({
    url,
    method,
    data,
    onDownloadProgress,
    signal,
    beforeRequest,
    afterRequest,
    options,
  })
}

export function post<T = any>(
  { url, data, method = 'POST', headers, onDownloadProgress, signal, beforeRequest, afterRequest, options }: HttpOption,
): Promise<T> {
  return http<T>({
    url,
    method,
    data,
    headers,
    onDownloadProgress,
    signal,
    beforeRequest,
    afterRequest,
    options
  })
}

/**
 * 下载文件专用方法，自动处理 application/octet-stream 或 blob 响应
 * @param url 请求地址
 * @param data 请求参数
 * @param method 请求方法，默认 POST
 * @param headers 请求头
 * @param onDownloadProgress 下载进度回调
 * @param signal 取消信号
 * @param beforeRequest 请求前钩子
 * @param afterRequest 请求后钩子
 * @param options 其他选项
 */
export async function downloadPPT({
  url,
  data,
  method = 'POST',
  headers,
  onDownloadProgress,
  signal,
  beforeRequest,
  afterRequest,
  options
}: HttpOption) {
  beforeRequest?.();
  try {
    const params = Object.assign(typeof data === 'function' ? data() : data ?? {}, {});

    const axiosConfig: any = {
      responseType: 'blob',
      headers,
      signal,
      onDownloadProgress
    };
    let response;
    if (method === 'GET') {
      response = await request.get(url, { params, ...axiosConfig });
    } else {
      response = await request.post(url, params, axiosConfig);
    }
    // 判断response.data是否为ppt文件
    if (response?.data.type !== 'application/octet-stream') {
      // 尝试解析 blob 错误信息
      try {
        const text = await response.data.text();
        throw new Error(text);
      } catch {
        throw new Error('导出PPT失败');
      }
    }
    // 处理文件名，优先级：后端 Content-Disposition > data.downloadName > 默认
    let fileName = data.downloadName;
    const disposition = response.headers['content-disposition'] || response.headers['Content-Disposition'];
    if (disposition) {
      const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/);
      if (match) {
        fileName = decodeURIComponent(match[1] || match[2] || fileName);
      }
    }
    // 若 fileName 为空，生成日期时间+随机字符
    if (!fileName) {
      const date = new Date();
      const randomString = Math.random().toString(36).substr(2, 9);
      fileName = `download_${data.productId}_${date.toISOString().split('T')[0]}_${randomString}.ppt`;
    }
    // 若 fileName 无 .ppt 后缀，自动补全
    if (!/\.pptx?$/i.test(fileName)) {
      fileName += '.ppt';
    }
    // 创建 blob 链接并下载
    const blob = new Blob([response.data]);
    // 兼容 IE/Edge 的 msSaveOrOpenBlob
    if (window.navigator && (window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }, 100);
    }
    afterRequest?.();
    return { success: true, fileName };
  } catch (error: any) {
    afterRequest?.();
    // 尝试解析 blob 错误信息
    if (error?.response?.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        throw new Error(text);
      } catch {
        throw new Error('导出PPT失败');
      }
    }
    throw new Error(error?.message || '导出PPT失败');
  }
}

export default post
